import { useMemo, useState } from "react";
import { siteList, siteCategories } from "@/mocks/siteListData";

interface SiteListProps {
  query: string;
}

export default function SiteList({ query }: SiteListProps) {
  const [active, setActive] = useState("全部");

  const filtered = useMemo(() => {
    const keyword = query.trim().toLowerCase();
    return siteList.filter((site) => {
      const matchCategory = active === "全部" || site.category === active;
      const matchKeyword =
        keyword.length === 0 ||
        site.title.toLowerCase().includes(keyword) ||
        site.summary.toLowerCase().includes(keyword) ||
        site.category.toLowerCase().includes(keyword) ||
        site.domain.toLowerCase().includes(keyword);
      return matchCategory && matchKeyword;
    });
  }, [active, query]);

  return (
    <section id="sites" className="scroll-mt-20 bg-background-100 border-y border-background-200">
      <div className="w-full px-4 md:px-6 py-14 md:py-20">
        <div className="mx-auto max-w-shell">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
            <div>
              <span className="text-sm text-accent-700 font-label">收录列表</span>
              <h2 className="mt-2 font-heading text-2xl md:text-3xl font-semibold text-foreground-950">
                标题配一句摘要，扫一眼就知道值不值得点
              </h2>
              <p className="mt-3 max-w-2xl text-sm md:text-base text-foreground-600 leading-relaxed">
                不堆砌链接，每条都写清用途。先看标题和摘要判断，再决定要不要进站，把挑选的时间省下来。
              </p>
            </div>
            <span className="text-sm text-foreground-500 whitespace-nowrap">
              当前显示 {filtered.length} / {siteList.length} 条
            </span>
          </div>

          <div className="mt-8 flex flex-wrap gap-2">
            {siteCategories.map((category) => (
              <button
                key={category}
                type="button"
                onClick={() => setActive(category)}
                className={`px-4 py-1.5 rounded-full text-sm transition-colors cursor-pointer ${
                  active === category
                    ? "bg-secondary-500 text-background-50"
                    : "bg-background-50 text-foreground-700 border border-background-300 hover:border-secondary-400"
                }`}
              >
                {category}
              </button>
            ))}
          </div>

          <div className="mt-8 rounded-lg bg-background-50 border border-background-200 divide-y divide-background-200">
            {filtered.length === 0 && (
              <div className="flex flex-col items-center gap-3 py-16 text-center">
                <i className="ri-search-eye-line text-3xl text-foreground-400"></i>
                <p className="text-sm text-foreground-600">
                  没有匹配的站点，换个关键词或分类试试
                </p>
                <button
                  type="button"
                  onClick={() => setActive("全部")}
                  className="px-4 py-2 rounded-md text-sm bg-secondary-100 text-secondary-900 hover:bg-secondary-200 transition-colors cursor-pointer"
                >
                  查看全部分类
                </button>
              </div>
            )}

            {filtered.map((site, index) => (
              <div
                key={site.title}
                className="flex flex-col md:flex-row md:items-center gap-3 md:gap-5 px-5 md:px-6 py-5"
              >
                <span className="hidden md:flex w-8 h-8 shrink-0 rounded-md bg-background-100 border border-background-200 items-center justify-center text-xs text-foreground-500 font-label">
                  {String(index + 1).padStart(2, "0")}
                </span>

                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <h3 className="font-heading text-base md:text-lg font-semibold text-foreground-950">
                      {site.title}
                    </h3>
                    <span className="px-2 py-0.5 rounded-full bg-accent-50 border border-accent-200 text-[11px] text-accent-800">
                      {site.badge}
                    </span>
                    <span className="px-2 py-0.5 rounded-full bg-secondary-100 text-[11px] text-secondary-900">
                      {site.category}
                    </span>
                  </div>
                  <p className="mt-2 text-sm text-foreground-600 leading-relaxed">
                    {site.summary}
                  </p>
                  <p className="mt-2 flex items-center gap-1.5 text-xs text-foreground-500">
                    <i className="ri-link"></i>
                    {site.domain}
                  </p>
                </div>

                <a
                  href="#sites"
                  className="shrink-0 flex items-center justify-center gap-2 px-4 md:px-5 py-2.5 rounded-md border border-primary-300 text-primary-700 text-sm hover:bg-primary-50 transition-colors whitespace-nowrap cursor-pointer"
                >
                  直达站点
                  <i className="ri-arrow-right-up-line"></i>
                </a>
              </div>
            ))}
          </div>

          <div className="mt-6 flex flex-col sm:flex-row items-center justify-between gap-3 rounded-lg bg-background-50 border border-background-200 px-5 py-4">
            <p className="text-sm text-foreground-600">
              没有找到想去的网站？可以提交收录，我们会先实际体验再决定。
            </p>
            <a
              href="#submit"
              className="flex items-center gap-2 px-4 py-2 rounded-md text-sm bg-primary-500 text-background-50 hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              <i className="ri-add-circle-line"></i>
              前往收录申请
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}